import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { parseApiError, useAuth, usePermission } from '@saas-kit/lib';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ErrorBanner,
  PageHeader,
} from '@saas-kit/ui';

import { listRoles, type Role } from './api';
import { RoleEditor } from './RoleEditor';
import { RolesList } from './RolesList';
import { TenantRequiredState } from '../../components/TenantRequiredState';

export function RolesPage(): JSX.Element {
  const { t } = useTranslation('rbac');
  const selectedTenant = useAuth((s) => s.selectedTenant);
  const canRead = usePermission('members.read');
  const canManage = usePermission('members.manage');
  const [params, setParams] = useSearchParams();
  const selectedId = params.get('role');
  const creating = params.get('new') === '1';

  const setParam = useCallback(
    (mutate: (next: URLSearchParams) => void): void => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          mutate(next);
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const select = (id: string): void =>
    setParam((next) => {
      next.delete('new');
      next.set('role', id);
    });
  const startCreate = (): void =>
    setParam((next) => {
      next.delete('role');
      next.set('new', '1');
    });
  const exitCreate = (): void =>
    setParam((next) => {
      next.delete('new');
    });

  const query = useQuery<Role[]>({
    queryKey: ['tenant-roles', selectedTenant?.tenant_id],
    queryFn: listRoles,
    enabled: Boolean(selectedTenant?.tenant_id && canRead),
  });

  const roles = useMemo(() => query.data ?? [], [query.data]);
  const customRoles = useMemo(() => roles.filter((role) => !role.is_system).length, [roles]);
  const selectedRole = useMemo(
    () =>
      creating ? null : roles.find((role) => role.id === selectedId) ?? roles[0] ?? null,
    [creating, roles, selectedId],
  );
  const mode: 'create' | 'edit' | 'empty' = creating
    ? 'create'
    : selectedRole
      ? 'edit'
      : 'empty';

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('title')}
        description={t('subtitle')}
        actions={
          <Link to="/team">
            <Button variant="secondary">{t('actions.open_members')}</Button>
          </Link>
        }
      />

      <div className="grid gap-4 md:grid-cols-3">
        <MetricCard label={t('metrics.total')} value={String(roles.length)} />
        <MetricCard label={t('metrics.custom')} value={String(customRoles)} />
        <MetricCard
          label={t('metrics.mode')}
          value={canManage ? t('metrics.mode_manage') : t('metrics.mode_read')}
        />
      </div>

      {!selectedTenant ? (
        <TenantRequiredState
          title={t('tenant_required.title')}
          body={t('tenant_required.body')}
          cta={t('tenant_required.cta')}
        />
      ) : !canRead ? (
        <ErrorBanner>{t('errors.read_denied')}</ErrorBanner>
      ) : query.isError ? (
        <ErrorBanner>{t(parseApiError(query.error).i18nKey)}</ErrorBanner>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>{t('workspace_section.title')}</CardTitle>
            <CardDescription>
              {canManage ? t('workspace_section.description') : t('workspace_section.read_only')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid min-h-[28rem] gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
              <RolesList
                roles={roles}
                selectedId={selectedRole?.id ?? null}
                onSelect={select}
                onCreateClick={startCreate}
                isLoading={query.isLoading}
                canManage={canManage}
                creating={creating}
              />
              <RoleEditor
                key={creating ? 'create' : (selectedRole?.id ?? 'empty')}
                mode={mode}
                role={selectedRole}
                onSaved={(saved) => select(saved.id)}
                onDeleted={() =>
                  setParam((next) => {
                    next.delete('role');
                    next.delete('new');
                  })
                }
                onCancelCreate={exitCreate}
              />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <Card>
      <CardContent className="p-5">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="mt-2 text-2xl font-semibold text-foreground">{value}</p>
      </CardContent>
    </Card>
  );
}
